import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import type { UseQueryResult } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import styled, { css } from 'styled-components';

import { CenterFlex, Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Spinner from '@/components/Common/Spinner';
import Transition from '@/components/Common/Transition';
import { MainText } from '@/components/Typography/NamedTypo';
import type { CreateChartPayload } from '@/entities/chart/api/types';
import { CHART_GRAPH } from '@/entities/chart/model/constants';
import type { ChartBaseWithName } from '@/entities/chart/model/types';
import DateRangeDisplay from '@/features/(charts)/chart-detail/ui/date-range-display/date-range-display';
import { useOpenChartClickDialog, useOpenChartWordCloudClickDialog } from '@/features/(charts)/chart-renderer/hooks';
import { buildFeedbackPeakAnalysisOptions } from '@/features/(charts)/chart-renderer/lib/build-feedback-peak-analysis-options';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import {
  fieldToWordCloudBasis,
  isWordCloudDatasetField,
  isWordCloudGraph,
} from '@/features/(charts)/chart-renderer/lib/word-cloud-basis';
import ChartRenderer from '@/features/(charts)/chart-renderer/ui/chart-renderer';
import useChartWordCloudItems from '@/features/(charts)/chart-renderer/ui/Single/use-chart-word-cloud-items';
import { TranslateLanguageSelect } from '@/features/WordCloud/components/TranslateLanguageSelect';
import { WordCloud } from '@/features/WordCloud/components/WordCloud';
import { useWordCloudAction } from '@/features/WordCloud/hooks/useWordCloudAction';
import { useWordCloudTranslation } from '@/features/WordCloud/hooks/useWordCloudTranslation';
import { DETAIL_MODAL_TYPE } from '@/stores/pure/DetailModalStore';
import { getTimezoneDayjs } from '@/utils/time';

type Props = {
  chartAlias: string;
  chart:
    | (ChartBase & { name: string })
    | (CreateChartPayload & { name: string });
  tableQuery: UseQueryResult<ChartTableRes>;
  onHistoricalChange: (value: boolean) => void;
};

const ReportChartProvider = (props: Props) => {
  const openWordCloudFeedbackListModal = useOpenChartWordCloudClickDialog();
  const openChartClickDialog = useOpenChartClickDialog();

  const { data: tableData, isFetching } = props.tableQuery;
  const isWordCloudChart = isWordCloudGraph(props.chart.graph);
  const wordCloudData = useChartWordCloudItems({
    chart: props.chart as ChartBase,
    tableData,
  });

  const {
    isSupported: isTranslationSupported,
    targetLanguage,
    setTargetLanguage,
    translatedWords,
    isTranslating,
  } = useWordCloudTranslation({
    words: wordCloudData,
    enabled: isWordCloudChart && wordCloudData.length > 0,
  });

  const onWordClick = (word: { text: string; originalKeyword?: string }) => {
    const dateRange = chartUtils.getTimeRangeByTerm(
      getTimezoneDayjs(),
      props.chart.target.term,
    );

    const keyword = word.originalKeyword || word.text;
    if (isWordCloudDatasetField(props.chart.field?.type)) {
      openWordCloudFeedbackListModal({
        keyword,
        translatedKeyword: word.text,
        basis: fieldToWordCloudBasis(props.chart.field?.type),
        peakAnalysis: props.chart.historical?.value
          ? buildFeedbackPeakAnalysisOptions(props.chart as ChartBaseWithName)
          : undefined,
        filter: {
          ...props.chart.target.filter,
          start_date: dateRange[0].toJSON(),
          end_date: dateRange[1].toJSON(),
        },
      });
      return;
    }

    openChartClickDialog({
      alias: props.chartAlias,
      type: DETAIL_MODAL_TYPE.CHART,
      options: {
        chart: {
          ...props.chart,
          graph: CHART_GRAPH.BAR,
        } as ChartBase & { name: string },
        isAll: false,
        cell: {
          compared_target: false,
          series_index: 0,
          item_x: keyword,
          item_x_name: word.text,
        },
      },
    });
  };

  const { handleCopyToClipboard, handleDownloadPNG } = useWordCloudAction();

  const hasWordCloudData = wordCloudData && wordCloudData.length > 0;

  return (
    <Style g={16}>
      <Transition
        css={css`
          display: flex;
          flex-direction: column;
          width: 100%;
          height: 100%;
        `}
        status={!isFetching && !isTranslating}
        fallback={
          <CenterFlex style={{ flex: 1, height: '100%' }}>
            <Spinner />
          </CenterFlex>
        }
        style={{ flex: 1, overflow: 'auto' }}
      >
        <ErrorBoundary
          fallbackRender={() => (
            <CenterFlex style={{ height: '100%' }}>
              <MainText dim>Something went wrong</MainText>
            </CenterFlex>
          )}
        >
          {tableData && !isWordCloudChart && (
            <ChartRenderer
              tableData={tableData}
              chart={props.chart as ChartBase & { name: string }}
              onHistoricalChange={props.onHistoricalChange}
            />
          )}
          {isWordCloudChart && (
            <WordCloud
              wordCloudId={props.chartAlias}
              words={translatedWords}
              onWordClick={onWordClick}
              basis={fieldToWordCloudBasis(props.chart.field?.type)}
            />
          )}
        </ErrorBoundary>
      </Transition>
      <Flex j="space-between" a="center">
        <DateRangeDisplay
          term={props.chart.target.term}
          compareTerm={props.chart.target.compare_term}
        />
        {hasWordCloudData && (
          <Flex g={8}>
            {isTranslationSupported && (
              <TranslateLanguageSelect
                value={targetLanguage}
                onChange={setTargetLanguage}
                isLoading={isTranslating}
              />
            )}
            {isWordCloudChart && (
              <>
                <IconButton
                  icon={<DownloadOutlined />}
                  sizeType="m"
                  onClick={() => handleDownloadPNG(props.chartAlias)}
                />
                <IconButton
                  icon={<CopyOutlined />}
                  sizeType="m"
                  onClick={() => handleCopyToClipboard(props.chartAlias)}
                />
              </>
            )}
          </Flex>
        )}
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  flex: 1;
  width: 100%;
  height: 100%;
`;

export default ReportChartProvider;
